import { useTranslate } from "@made-people/grease-translations";
import { LinkAsLink } from "@made-people/blueprint-link";
import { ResponsiveImage } from "@made-people/blueprint-responsive-image";
import type { Order } from "@made-people/grease-order";
import {
  MAX_VISIBLE_THUMBNAILS,
  getOrderItemImages,
  getOrderDate,
  getOrderTotalQuantity,
} from "./utils";

export const OrderCard = ({
  order,
  viewOrderHref,
}: {
  order: Order;
  viewOrderHref: string | undefined;
}) => {
  const translate = useTranslate();
  const images = getOrderItemImages(order);
  const visibleImages = images.slice(0, MAX_VISIBLE_THUMBNAILS);
  const overflowCount = images.length - MAX_VISIBLE_THUMBNAILS;
  const statusLabel = translate(`Account_Order_Status_${order.status}`);
  const totalQuantity = getOrderTotalQuantity(order);

  return (
    <div className="border-b first:border-t border-border-light p-4 rounded-md bg-[#f3f2ef] max-w-[700px]">
      <div className="grid grid-cols-2 gap-y-2 lg:gap-y-3 md:grid-cols-[1fr_1fr_16rem] md:gap-x-6 items-baseline md:items-start">
        <span className="text-label-md font-medium uppercase tracking-wide lg:font-medium">
          {translate("Account_OrderDetails_OrderNumber", {
            orderNumber: order.orderNumber,
          })}
        </span>
        <span className="text-label-md text-right md:text-left md:uppercase">
          {getOrderDate(order)}
        </span>

        <span className="text-label-md capitalize">{statusLabel}</span>
        <span className="text-label-md text-right md:text-left uppercase">
          {order.totals.grandTotal.formatted}
        </span>

        {(visibleImages.length > 0 || viewOrderHref) && (
          <div className="col-span-2 md:col-span-1 md:row-span-3 md:col-start-3 md:row-start-1 flex items-end lg:items-center gap-0.5 md:gap-1 pt-3 md:pt-0">
            {visibleImages.map((src, index) => (
              <ResponsiveImage
                key={`img-${index}`}
                image={{ src, alt: "" }}
                loading="lazy"
                displaySize="56px"
                className="w-14 aspect-product-image object-cover"
              />
            ))}
            {overflowCount > 0 && (
              <span className="text-label-md uppercase px-2">
                +{overflowCount}
              </span>
            )}
            {viewOrderHref && (
              <div className="ml-auto md:hidden">
                <LinkAsLink
                  label={translate("Account_View_Order")}
                  link={{ href: viewOrderHref }}
                  variants={{ style: "underline" }}
                  className="text-label-md"
                />
              </div>
            )}
          </div>
        )}

        {viewOrderHref && (
          <LinkAsLink
            label={translate("Account_View_Order")}
            link={{ href: viewOrderHref }}
            variants={{ style: "underline" }}
            className="text-label-md hidden md:inline-flex"
          />
        )}

        <span className="hidden md:inline text-body-sm lg:text-label-md capitalize">
          {translate("Account_Order_Item_Quantity", {
            quantity: totalQuantity,
          })}
        </span>
      </div>
    </div>
  );
};
